import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, User, UserPlus, Copy, Check, X, GraduationCap, BedDouble, Upload, Download, IndianRupee, Link2, Plus, FileText, Wallet, AlertCircle } from "lucide-react";
import CollectPaymentButton from "@/components/admin/CollectPaymentButton";
import CashCollectButton from "@/components/admin/CashCollectButton";
import RefundButton from "@/components/admin/RefundButton";
import WardWallet from "@/components/admin/WardWallet";
import KycVaultModal from "@/components/admin/KycVaultModal";

function TempPasswordCard({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(
      `HTP Sports Hub · Parent Portal\nLogin: ${data.email}\nTemporary password: ${data.temp_password}\nPlease log in at /parent/login and change your password immediately.`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mb-6 border-2 border-brand-orange bg-brand-orange/10 p-5" data-testid="parent-temp-password-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">// Share once · temporary password</div>
          <div className="font-bold text-lg">{data.name}</div>
          <div className="text-sm text-white/70">{data.email}</div>
          <div className="mt-3 font-mono text-2xl brand-blue tracking-wider select-all" data-testid="parent-temp-pw-value">{data.temp_password}</div>
          <div className="text-xs text-white/50 mt-2">
            Displayed <b>once only</b>. Share with the parent securely (WhatsApp, phone, print). They&apos;ll be forced to change it on first login.
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button onClick={copy} data-testid="parent-temp-copy" className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white">
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
          <button onClick={onClose} data-testid="parent-temp-close" className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-2 hover:bg-white/10">
            <X size={14} /> Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function FeeLine({ label, value, onChange, refundable, testId, note }) {
  return (
    <div className="grid grid-cols-[1fr_140px] items-center gap-3 py-2.5 border-b border-white/6 last:border-0">
      <div>
        <div className="text-sm">{label}</div>
        {note && <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-0.5">{note}</div>}
        {refundable && <div className="text-[10px] font-mono uppercase tracking-widest brand-blue mt-0.5">Refundable at exit</div>}
      </div>
      <div className="flex items-center border border-white/20 bg-[#0B1120] focus-within:border-brand-orange">
        <span className="pl-2 text-white/40 text-sm">₹</span>
        <input
          type="number" min="0" step="1" value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full bg-transparent py-2 pr-2 font-mono text-sm text-right outline-none"
        />
      </div>
    </div>
  );
}

function EnrollModal({ parent, batches, dormBeds, onClose, onDone }) {
  const [wardId, setWardId] = useState(parent.wards[0]?.id || "");
  const [batchId, setBatchId] = useState("");
  // Fee structure (radio): monthly | quarterly | custom
  const [feeCode, setFeeCode] = useState("");
  const [monthlyAmount, setMonthlyAmount] = useState("");
  // Editable one-time fees — prefilled from batch config
  const [regFee, setRegFee] = useState("");
  const [admFee, setAdmFee] = useState("");
  const [kitFee, setKitFee] = useState("");
  const [secDep, setSecDep] = useState("");
  // Dormitory add-on
  const [includeDorm, setIncludeDorm] = useState(false);
  const [dormBed, setDormBed] = useState("");
  const [dormFee, setDormFee] = useState(dormBeds?.monthly_fee || 6500);
  const [hostelReg, setHostelReg] = useState("");
  const [hostelAdm, setHostelAdm] = useState("");
  const [hostelSec, setHostelSec] = useState("");
  // Wallet top-up (optional, collected at desk)
  const [walletAmt, setWalletAmt] = useState("");
  const [walletMethod, setWalletMethod] = useState("cash");
  const [walletTxnRef, setWalletTxnRef] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedBatch = batches.find((b) => b.id === batchId);
  const feeStructures = selectedBatch?.fee_structures || [];

  // When batch changes, prefill all editable fees from batch config
  useEffect(() => {
    if (!selectedBatch) return;
    setRegFee(String(selectedBatch.registration_fee ?? 0));
    setAdmFee(String(selectedBatch.admission_fee ?? 0));
    setKitFee(String(selectedBatch.kit_fee ?? 0));
    setSecDep(String(selectedBatch.security_deposit ?? 0));
    setFeeCode("");
    setMonthlyAmount("");
  }, [batchId]);

  // When fee structure is picked (monthly/quarterly), auto-fill monthly amount from batch
  useEffect(() => {
    if (!selectedBatch || !feeCode) return;
    if (feeCode === "monthly")   setMonthlyAmount(String(selectedBatch.monthly_fee || ""));
    if (feeCode === "quarterly") setMonthlyAmount(String(selectedBatch.quarterly_fee || ""));
    if (feeCode === "custom" && !monthlyAmount) setMonthlyAmount("");
    // Any structure listed by backend fee_structures[]
    const fs = feeStructures.find((f) => f.code === feeCode);
    if (fs && fs.amount) setMonthlyAmount(String(fs.amount));
  }, [feeCode, batchId]);

  // Preload hostel defaults the moment the toggle turns on
  useEffect(() => {
    if (!includeDorm) return;
    let ignore = false;
    api.get("/admin/hostel-settings").then(({ data }) => {
      if (ignore) return;
      if (hostelReg === "") setHostelReg(String(data.registration_fee ?? ""));
      if (hostelAdm === "") setHostelAdm(String(data.admission_fee ?? ""));
      if (hostelSec === "") setHostelSec(String(data.security_deposit ?? ""));
    }).catch(() => {});
    return () => { ignore = true; };
  }, [includeDorm]);

  // Live "Total to bill today"
  const total = useMemo(() => {
    const n = (v) => Math.max(0, parseFloat(v) || 0);
    let t = n(regFee) + n(admFee) + n(kitFee) + n(secDep) + n(monthlyAmount);
    if (includeDorm) {
      t += n(dormFee) + n(hostelReg) + n(hostelAdm) + n(hostelSec);
    }
    return t;
  }, [regFee, admFee, kitFee, secDep, monthlyAmount, includeDorm, dormFee, hostelReg, hostelAdm, hostelSec]);

  const submit = async (e) => {
    e.preventDefault();
    if (!wardId || !batchId || !feeCode) {
      toast.error("Pick a ward, batch and fee structure to continue");
      return;
    }
    if (walletAmt && parseFloat(walletAmt) > 0) {
      if (walletMethod !== "cash" && !walletTxnRef.trim()) {
        toast.error("Wallet top-up needs a txn/ref for non-cash methods");
        return;
      }
    }
    setSaving(true);
    try {
      const numOrNull = (v) => v === "" || v === null ? null : parseFloat(v);
      const body = {
        parent_id: parent.id,
        ward_id: wardId,
        batch_id: batchId,
        fee_structure: feeCode,
        registration_fee_override: numOrNull(regFee),
        admission_fee_override:    numOrNull(admFee),
        kit_fee_override:          numOrNull(kitFee),
        security_deposit_override: numOrNull(secDep),
      };
      // Only send custom_fee if the founder edited the monthly line for a custom plan
      if (feeCode === "custom") body.custom_fee = parseFloat(monthlyAmount) || 0;
      else if (monthlyAmount !== "") body.custom_fee = parseFloat(monthlyAmount) || 0;

      if (includeDorm && dormBed) {
        const [room, bed] = dormBed.split("/");
        body.dormitory_bed = { room, bed };
        body.dormitory_fee = parseFloat(dormFee) || undefined;
        if (hostelReg !== "") body.dormitory_registration_fee = parseFloat(hostelReg) || 0;
        if (hostelAdm !== "") body.dormitory_admission_fee = parseFloat(hostelAdm) || 0;
        if (hostelSec !== "") body.dormitory_security_deposit = parseFloat(hostelSec) || 0;
      }

      if (walletAmt && parseFloat(walletAmt) > 0) {
        body.wallet_topup_amount = parseFloat(walletAmt);
        body.wallet_topup_method = walletMethod;
        body.wallet_topup_txn_ref = walletTxnRef.trim() || null;
      }

      const { data } = await api.post("/admin/enrollments", body);
      let msg = `Enrolled ${data.enrollment.ward_name} in ${data.enrollment.batch_name} · billed ₹${data.total_billed}`;
      if (data.dormitory_invoice) msg += " + hostel";
      if (data.wallet_topup) msg += ` + ₹${data.wallet_topup.amount} wallet top-up`;
      toast.success(msg);
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#131A2B] border border-white/12 p-6 md:p-8 max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="enroll-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-1">// Academy · New enrolment</div>
            <div className="font-display text-2xl uppercase font-black">Enrol {parent.name}&apos;s ward</div>
            <div className="text-[11px] text-white/50 mt-1">
              Every fee is editable — override any line for scholarships, or leave at the batch default. Everything is billed in one shot below.
            </div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {/* Step 1: Ward + Batch */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Ward</div>
              <select value={wardId} onChange={(e) => setWardId(e.target.value)} required
                data-testid="enroll-ward-select"
                className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 focus:outline-none focus:border-brand-blue">
                <option value="">Select ward…</option>
                {parent.wards.map((w) => (
                  <option key={w.id} value={w.id}>{w.name} · Age {w.age} · {w.sport}</option>
                ))}
              </select>
              {parent.wards.length === 0 && (
                <div className="text-xs text-yellow-400 mt-1">This parent has no wards. Add one from Parents & Wards first.</div>
              )}
            </label>
            <label>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Academy batch</div>
              <select value={batchId} onChange={(e) => setBatchId(e.target.value)} required
                data-testid="enroll-batch-select"
                className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 focus:outline-none focus:border-brand-blue">
                <option value="">Select batch…</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.sport.toUpperCase()} · {b.age_group} · {b.name}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Step 2: Bill Preview (only shown once a batch is picked) */}
          {selectedBatch && (
            <div className="border-2 border-brand-blue/40 bg-brand-blue/5 p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">// Bill preview · edit any line</div>
                  <div className="font-display text-lg font-black">Enrolment charges</div>
                </div>
                <button type="button" onClick={() => {
                  setRegFee(String(selectedBatch.registration_fee ?? 0));
                  setAdmFee(String(selectedBatch.admission_fee ?? 0));
                  setKitFee(String(selectedBatch.kit_fee ?? 0));
                  setSecDep(String(selectedBatch.security_deposit ?? 0));
                  if (feeCode === "monthly")   setMonthlyAmount(String(selectedBatch.monthly_fee || ""));
                  if (feeCode === "quarterly") setMonthlyAmount(String(selectedBatch.quarterly_fee || ""));
                }}
                  data-testid="fees-reset-defaults"
                  className="text-[10px] font-mono uppercase tracking-widest text-brand-blue border border-brand-blue/40 px-2 py-1 hover:bg-brand-blue hover:text-white">
                  ↻ Reset from batch
                </button>
              </div>

              {/* Hint when the batch has no one-time fees configured — most
                  common admin surprise. Guides them to Batches & Fees. */}
              {[selectedBatch.registration_fee, selectedBatch.admission_fee,
                selectedBatch.kit_fee, selectedBatch.security_deposit]
                .every((v) => !v || parseFloat(v) === 0) && (
                <div className="mb-3 border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 text-[11px] p-2.5 flex items-start gap-2" data-testid="no-onetime-fees-hint">
                  <AlertCircle size={13} className="mt-0.5 shrink-0" />
                  <span>
                    This batch has <b>no one-time fees</b> configured. Either type overrides below, or open <b>Batches &amp; Fees</b> to set Registration / Admission / Kit / Security once for every future enrolment.
                  </span>
                </div>
              )}

              <FeeLine label="Registration Fee" value={regFee} onChange={setRegFee}
                testId="fee-reg" note="One-time, non-refundable" />
              <FeeLine label="Admission Fee" value={admFee} onChange={setAdmFee}
                testId="fee-adm" note="One-time, non-refundable" />
              <FeeLine label="Kit / Uniform Fee" value={kitFee} onChange={setKitFee}
                testId="fee-kit" note="One-time · set 0 to skip" />
              <FeeLine label="Security Deposit" value={secDep} onChange={setSecDep}
                testId="fee-sec" refundable />

              <div className="border-t border-white/12 mt-3 pt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Recurring plan</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    { code: "monthly",   label: "Monthly",   amt: selectedBatch.monthly_fee },
                    { code: "quarterly", label: "Quarterly", amt: selectedBatch.quarterly_fee },
                    ...feeStructures.filter((fs) => !["monthly","quarterly"].includes(fs.code)).map((fs) => ({ code: fs.code, label: fs.label, amt: fs.amount })),
                    { code: "custom",    label: "Custom",    amt: null },
                  ].map((opt) => (
                    <label key={opt.code}
                      data-testid={`enroll-fee-${opt.code}`}
                      className={`border p-3 cursor-pointer text-center ${feeCode === opt.code ? "border-brand-orange bg-brand-orange/10" : "border-white/12 hover:border-white/30"}`}>
                      <input type="radio" name="fee" value={opt.code} checked={feeCode === opt.code}
                        onChange={(e) => setFeeCode(e.target.value)} className="mr-2 accent-brand-orange" />
                      <span className="font-bold text-sm">{opt.label}</span>
                      {opt.amt != null && <div className="text-[11px] brand-blue font-mono mt-1">{formatINR(opt.amt)}</div>}
                    </label>
                  ))}
                </div>
                {feeCode && (
                  <div className="mt-3">
                    <FeeLine
                      label={feeCode === "custom" ? "Custom amount" : `${feeCode.charAt(0).toUpperCase()+feeCode.slice(1)} fee`}
                      value={monthlyAmount} onChange={setMonthlyAmount}
                      testId="fee-monthly" note="First installment billed today" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Hostel add-on */}
          {selectedBatch && (
            <div className="border border-white/12 p-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeDorm} onChange={(e) => setIncludeDorm(e.target.checked)}
                  data-testid="enroll-dorm-toggle" className="accent-brand-orange" />
                <BedDouble size={16} className="brand-orange" />
                <span className="font-bold text-sm">Add residential hostel bed</span>
                <span className="text-xs text-white/50">(for students staying on campus)</span>
              </label>
              {includeDorm && (
                <div className="mt-4 space-y-3">
                  <label className="block">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Bed</div>
                    <select value={dormBed} onChange={(e) => setDormBed(e.target.value)} required
                      data-testid="enroll-dorm-bed"
                      className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue">
                      <option value="">Select an available bed…</option>
                      {(dormBeds?.beds || []).filter((b) => !b.occupied).map((b) => (
                        <option key={`${b.room}/${b.bed}`} value={`${b.room}/${b.bed}`}>Room {b.room} · Bed {b.bed}</option>
                      ))}
                    </select>
                  </label>
                  <div className="border border-brand-orange/30 bg-brand-orange/5 p-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-2">// Hostel charges — editable</div>
                    <FeeLine label="Hostel Registration" value={hostelReg} onChange={setHostelReg} testId="enroll-hostel-reg" note="One-time" />
                    <FeeLine label="Hostel Admission"    value={hostelAdm} onChange={setHostelAdm} testId="enroll-hostel-adm" note="One-time" />
                    <FeeLine label="Hostel Security Deposit" value={hostelSec} onChange={setHostelSec} testId="enroll-hostel-sec" refundable />
                    <FeeLine label="Hostel Monthly Rent" value={dormFee}  onChange={setDormFee}  testId="enroll-dorm-fee" note="First month billed today" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Optional wallet top-up */}
          {selectedBatch && (
            <div className="border border-white/12 p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">// Optional · wallet top-up</div>
                  <div className="text-sm font-bold flex items-center gap-2"><Wallet size={14} className="brand-blue" /> Initial wallet balance</div>
                </div>
                <div className="text-[11px] text-white/50">Skip to add later from ward profile</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Amount ₹</div>
                  <input type="number" min="0" value={walletAmt} onChange={(e) => setWalletAmt(e.target.value)}
                    data-testid="enroll-wallet-amt" placeholder="0"
                    className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue" />
                </label>
                <label>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Method</div>
                  <select value={walletMethod} onChange={(e) => setWalletMethod(e.target.value)}
                    data-testid="enroll-wallet-method"
                    className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm">
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                    <option value="cheque">Cheque</option>
                    <option value="bank_transfer">Bank transfer</option>
                  </select>
                </label>
                <label>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Txn / Ref</div>
                  <input value={walletTxnRef} onChange={(e) => setWalletTxnRef(e.target.value)}
                    data-testid="enroll-wallet-ref"
                    placeholder={walletMethod === "cash" ? "Optional" : "Required"}
                    className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm font-mono focus:outline-none focus:border-brand-blue" />
                </label>
              </div>
            </div>
          )}

          {/* Total + Submit */}
          <div className="border-t border-white/15 pt-4 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Total to bill today</div>
              <div className="font-display text-3xl font-black brand-orange" data-testid="enroll-total">
                {formatINR(total)}
              </div>
              {walletAmt && parseFloat(walletAmt) > 0 && (
                <div className="text-[11px] text-white/50 mt-0.5">+ ₹{parseFloat(walletAmt).toFixed(0)} wallet (separate collection)</div>
              )}
            </div>
            <button type="submit" disabled={saving || !selectedBatch}
              data-testid="enroll-submit"
              className="bg-brand-blue text-white px-8 py-3 font-bold uppercase tracking-widest hover:glow-blue disabled:opacity-50 shrink-0">
              {saving ? "Enrolling…" : "Enrol & Generate Bill"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/parents/bulk-import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      toast.success(`${data.created_count} created · ${data.skipped_count} skipped`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadCredentials = () => {
    if (!report?.credentials_csv) return;
    const blob = new Blob([report.credentials_csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `parent-credentials-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="bulk-import-modal">
      <div className="bg-[#131A2B] border border-white/12 p-8 max-w-2xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-1">// Bulk import · CSV</div>
            <div className="font-display text-2xl uppercase font-black">Import parents from CSV</div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        {!report ? (
          <>
            <p className="text-sm text-white/60 mb-4">
              Upload a CSV with columns: <span className="font-mono brand-blue">name</span>, <span className="font-mono brand-blue">email</span>, <span className="font-mono brand-blue">phone</span> (optional), <span className="font-mono brand-blue">temp_password</span> (optional — auto-generated if blank).
            </p>
            <div className="border border-white/10 bg-black/40 p-4 font-mono text-[11px] text-white/60 mb-4 whitespace-pre">
{`name,email,phone,temp_password
Riya Sharma,riya@example.com,+919812345678,
Vikram Iyer,vikram@example.com,+919812345679,MyOwnPass123`}
            </div>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="bulk-file"
              className="block w-full text-sm text-white/80 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:font-mono file:uppercase file:tracking-widest file:bg-brand-blue file:text-white file:cursor-pointer" />
            <div className="flex items-center gap-3 mt-6">
              <button onClick={upload} disabled={!file || busy} data-testid="bulk-upload"
                className="flex-1 bg-brand-blue text-white py-3 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-40">
                {busy ? "Uploading…" : "Import"}
              </button>
              <button onClick={onClose} className="px-6 border border-white/20 text-sm">Cancel</button>
            </div>
          </>
        ) : (
          <div data-testid="bulk-report">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border border-brand-blue/40 bg-brand-blue/10 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Created</div>
                <div className="font-display text-3xl brand-blue">{report.created_count}</div>
              </div>
              <div className="border border-brand-orange/40 bg-brand-orange/10 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Skipped</div>
                <div className="font-display text-3xl brand-orange">{report.skipped_count}</div>
              </div>
            </div>

            {report.created_count > 0 && (
              <button onClick={downloadCredentials} data-testid="bulk-download-creds"
                className="w-full flex items-center justify-center gap-2 bg-brand-orange text-white py-3 font-bold text-sm uppercase tracking-widest hover:opacity-90 mb-6">
                <Download size={14} /> Download credentials CSV
              </button>
            )}

            {report.skipped.length > 0 && (
              <div className="mb-4">
                <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-2">// Skipped rows</div>
                <div className="border border-white/12 bg-black/40 max-h-40 overflow-y-auto">
                  {report.skipped.map((s, i) => (
                    <div key={i} className="grid grid-cols-6 text-xs px-3 py-2 border-b border-white/8">
                      <div className="col-span-1 font-mono">Row {s.row}</div>
                      <div className="col-span-2 truncate">{s.email || "—"}</div>
                      <div className="col-span-3 text-red-400 text-[11px]">{s.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={onClose} className="w-full border border-white/20 py-3 text-xs uppercase font-mono tracking-widest">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminParents() {
  const [parents, setParents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [dormBeds, setDormBeds] = useState(null);
  const [open, setOpen] = useState({});
  const [walletFor, setWalletFor] = useState(null); // {id, name}
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", temp_password: "" });
  const [tempResult, setTempResult] = useState(null);
  const [enrollParent, setEnrollParent] = useState(null);
  const [addWardParent, setAddWardParent] = useState(null);
  const [withdrawEnrol, setWithdrawEnrol] = useState(null);
  const [kycFor, setKycFor] = useState(null); // { target: "ward"|"parent", id, name }
  const [memberFilter, setMemberFilter] = useState({ q: "", kind: "all" });
  const [wardRequests, setWardRequests] = useState([]);

  const loadWardRequests = () => {
    api.get("/admin/ward-requests?status=pending")
      .then((r) => setWardRequests(r.data || []))
      .catch(() => {});
  };
  useEffect(() => { loadWardRequests(); }, []);

  const filteredParents = useMemo(() => {
    const q = (memberFilter.q || "").trim().toLowerCase();
    return parents.filter((p) => {
      const wc = (p.wards || []).length;
      if (memberFilter.kind === "parents" && wc === 0) return false;
      if (memberFilter.kind === "members" && wc > 0) return false;
      if (!q) return true;
      return (p.name || "").toLowerCase().includes(q)
          || (p.email || "").toLowerCase().includes(q)
          || (p.phone || "").includes(q)
          || (p.hub_id || "").toLowerCase().includes(q);
    });
  }, [parents, memberFilter]);

  const reactivate = async (enrollment_id) => {
    if (!window.confirm("Reactivate this enrolment? Auto-invoicing will resume next month.")) return;
    try {
      await api.post(`/admin/enrollments/${enrollment_id}/reactivate`);
      toast.success("Enrolment reactivated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reactivate failed"); }
  };

  const load = async () => {
    const [p, b, d] = await Promise.all([
      api.get("/admin/parents"),
      api.get("/admin/batches"),
      api.get("/admin/dormitory-beds"),
    ]);
    setParents(p.data);
    setBatches(b.data);
    setDormBeds(d.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-expand a parent + scroll to their card when navigated with ?parent=<id>
  useEffect(() => {
    if (!parents.length) return;
    const pid = new URLSearchParams(window.location.search).get("parent");
    if (!pid) return;
    if (!parents.find((p) => p.id === pid)) return;
    setOpen((o) => ({ ...o, [pid]: true }));
    // Give React one frame to render the expanded row, then scroll
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="parent-${pid}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  }, [parents]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/parents", form);
      toast.success("Parent account created");
      setTempResult(data);
      setForm({ name: "", email: "", phone: "", temp_password: "" });
      setShowCreate(false);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="admin-parents">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// ERM · Facility Members</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Facility Members.</h1>
          <p className="text-sm text-white/60 mt-2">All hub member accounts. Filter to find who to enrol a ward for — or add wards for members who requested one.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowBulk(true)}
            data-testid="admin-bulk-import-btn"
            className="flex items-center gap-2 border border-brand-orange text-brand-orange px-5 py-3 font-bold text-sm uppercase tracking-widest hover:bg-brand-orange hover:text-white"
          >
            <Upload size={16} /> Bulk import CSV
          </button>
          <button
            onClick={() => setShowCreate(!showCreate)}
            data-testid="admin-add-parent-btn"
            className="flex items-center gap-2 bg-brand-blue text-white px-5 py-3 font-bold text-sm uppercase tracking-widest hover:glow-blue"
          >
            <UserPlus size={16} /> {showCreate ? "Close" : "Create parent"}
          </button>
        </div>
      </div>

      {tempResult && <TempPasswordCard data={tempResult} onClose={() => setTempResult(null)} />}

      {showCreate && (
        <form onSubmit={submit} className="border border-white/12 bg-[#131A2B] p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-create-parent-form">
          <input
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Parent full name" required data-testid="parent-form-name"
            className="bg-transparent border border-white/20 py-3 px-4 focus:outline-none focus:border-brand-blue"
          />
          <input
            type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email (used for login)" required data-testid="parent-form-email"
            className="bg-transparent border border-white/20 py-3 px-4 focus:outline-none focus:border-brand-blue"
          />
          <input
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+91 phone (for WhatsApp)" data-testid="parent-form-phone"
            className="bg-transparent border border-white/20 py-3 px-4 focus:outline-none focus:border-brand-blue"
          />
          <input
            value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })}
            placeholder="Temporary password (min 6)" required minLength={6} data-testid="parent-form-temppw"
            className="bg-transparent border border-white/20 py-3 px-4 focus:outline-none focus:border-brand-blue"
          />
          <button
            type="submit" data-testid="parent-form-submit"
            className="md:col-span-2 bg-brand-blue text-white py-3 font-bold text-sm uppercase tracking-widest hover:glow-blue"
          >
            Create Account & Show Password
          </button>
        </form>
      )}

      {/* Filter + search + pending ward-request queue */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <input value={memberFilter.q} onChange={(e) => setMemberFilter({ ...memberFilter, q: e.target.value })}
            placeholder="Search by name, email, phone or Hub-ID…"
            data-testid="members-search"
            className="w-full bg-[#131A2B] border border-white/15 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-orange" />
        </div>
        {[
          { code: "all", label: `All (${parents.length})` },
          { code: "parents", label: `Parents · has wards (${parents.filter((p) => (p.wards || []).length > 0).length})` },
          { code: "members", label: `Members · no wards (${parents.filter((p) => (p.wards || []).length === 0).length})` },
        ].map((f) => (
          <button key={f.code} onClick={() => setMemberFilter({ ...memberFilter, kind: f.code })}
            data-testid={`members-filter-${f.code}`}
            className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest border ${memberFilter.kind === f.code ? "border-brand-orange bg-brand-orange text-black" : "border-white/15 hover:bg-white/5"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Ward-request approval queue (auto-shown when pending requests exist) */}
      {wardRequests.length > 0 && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 mb-6" data-testid="ward-requests-panel">
          <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300 mb-2">
            // {wardRequests.length} pending ward request{wardRequests.length === 1 ? "" : "s"}
          </div>
          <div className="space-y-2">
            {wardRequests.map((req) => (
              <div key={req.id} className="flex flex-wrap items-center justify-between gap-3 bg-[#131A2B] border border-white/10 p-3" data-testid={`ward-request-${req.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="text-sm">
                    <b>{req.ward_name}</b>
                    {req.sport && <span className="text-white/60"> · {req.sport}</span>}
                    {req.age && <span className="text-white/60"> · age {req.age}</span>}
                  </div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    Requested by <b className="text-white/80">{req.requester_name}</b>
                    {req.requester_email && <span> · {req.requester_email}</span>}
                    {req.requester_phone && <span> · {req.requester_phone}</span>}
                  </div>
                  {req.notes && <div className="text-[10px] text-white/40 mt-1">Note: {req.notes}</div>}
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => {
                    try {
                      await api.post(`/admin/ward-requests/${req.id}/approve`);
                      toast.success(`Approved · ${req.ward_name} added under ${req.requester_name}`);
                      loadWardRequests();
                      load();
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                    data-testid={`approve-request-${req.id}`}
                    className="border border-green-500/50 text-green-300 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest hover:bg-green-500 hover:text-black">
                    Approve
                  </button>
                  <button onClick={async () => {
                    try {
                      await api.post(`/admin/ward-requests/${req.id}/reject`);
                      toast.success("Request rejected");
                      loadWardRequests();
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                    data-testid={`reject-request-${req.id}`}
                    className="border border-white/25 text-white/60 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest hover:bg-white/10">
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filteredParents.map((p) => (
          <div key={p.id} className="border border-white/12 bg-[#131A2B]" data-testid={`parent-${p.id}`}>
            <div className="w-full p-5 flex items-center justify-between hover:bg-white/6">
              <button
                onClick={() => setOpen({ ...open, [p.id]: !open[p.id] })}
                className="flex items-center gap-4 flex-1 text-left"
              >
                <div className="w-10 h-10 border border-white/20 flex items-center justify-center"><User size={16} /></div>
                <div>
                  <div className="font-bold flex items-center gap-2">
                    {p.name}
                    {p.must_change_password && <span className="text-[9px] font-mono uppercase brand-orange border border-brand-orange/50 px-1.5 py-0.5">Pending pw change</span>}
                  </div>
                  <div className="text-xs font-mono text-white/50">{p.email} · {p.phone}</div>
                </div>
              </button>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-xs font-mono text-white/40 uppercase">Wards</div>
                  <div className="font-display brand-blue">{p.wards.length}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono text-white/40 uppercase">Invoices</div>
                  <div className="font-display brand-orange">{p.invoices.length}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono text-white/40 uppercase">KYC</div>
                  <div className={`font-display text-sm ${
                    (p.kyc_present || 0) >= (p.kyc_total_required || 0) ? "text-green-400" : "text-yellow-400"
                  }`} data-testid={`parent-kyc-badge-${p.id}`}>
                    {p.kyc_present || 0}/{p.kyc_total_required || 0}
                  </div>
                </div>
                <button
                  onClick={() => setKycFor({ target: "parent", id: p.id, name: p.name })}
                  data-testid={`parent-kyc-btn-${p.id}`}
                  className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest border border-white/20 text-white/80 px-3 py-2 hover:border-brand-orange hover:text-brand-orange"
                >
                  <FileText size={12} /> KYC
                </button>
                <button
                  onClick={() => setAddWardParent(p)}
                  data-testid={`add-ward-btn-${p.id}`}
                  className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest border border-white/20 text-white/80 px-3 py-2 hover:border-brand-blue hover:text-white"
                >
                  <Plus size={12} /> Add Ward
                </button>
                <button
                  onClick={() => setEnrollParent(p)}
                  disabled={p.wards.length === 0}
                  data-testid={`enroll-btn-${p.id}`}
                  className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <GraduationCap size={12} /> Enrol
                </button>
                <button
                  onClick={() => setOpen({ ...open, [p.id]: !open[p.id] })}
                  className="text-white/40"
                >
                  {open[p.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              </div>
            </div>
            {open[p.id] && (
              <div className="border-t border-white/12 p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-3">Wards</div>
                  {p.wards.map((w) => (
                    <div key={w.id} className="text-sm mb-2 flex items-start justify-between gap-2 border-b border-white/6 pb-2 last:border-b-0">
                      <div>
                        <div className="font-bold flex items-center gap-2">
                          {w.name}
                          <span
                            className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                              (w.kyc_present || 0) >= (w.kyc_total_required || 0)
                                ? "border-green-500/40 text-green-400"
                                : "border-yellow-500/40 text-yellow-400"
                            }`}
                            data-testid={`ward-kyc-badge-${w.id}`}
                          >
                            KYC {w.kyc_present || 0}/{w.kyc_total_required || 0}
                          </span>
                        </div>
                        <div className="text-xs text-white/50">Age {w.age} · {w.sport}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <button onClick={() => setKycFor({ target: "ward", id: w.id, name: w.name })}
                          data-testid={`ward-kyc-btn-${w.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest border border-white/25 text-white/80 px-2 py-1 hover:border-brand-orange hover:text-brand-orange transition flex items-center gap-1">
                          <FileText size={11} /> KYC
                        </button>
                        <button onClick={() => setWalletFor({ id: w.id, name: w.name })}
                          data-testid={`ward-wallet-btn-${w.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest border border-brand-orange text-brand-orange px-2 py-1 hover:bg-brand-orange hover:text-black transition flex items-center gap-1">
                          <IndianRupee size={11} /> Wallet
                        </button>
                      </div>
                    </div>
                  ))}
                  {p.wards.length === 0 && <div className="text-white/40 text-sm">No wards</div>}
                </div>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-3">Enrollments</div>
                  {p.enrollments.map((e) => {
                    const withdrawn = e.status === "withdrawn";
                    return (
                    <div key={e.id} className={`text-sm mb-2 border-b border-white/6 pb-2 last:border-b-0 ${withdrawn ? "opacity-60" : ""}`} data-testid={`enrol-row-${e.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className={`${withdrawn ? "text-white/50 line-through" : "brand-blue"}`}>{e.batch_name}</div>
                          <div className="text-xs text-white/50">{e.ward_name} · {e.fee_label || e.fee_structure} · {formatINR(e.monthly_fee)}</div>
                          {e.dormitory_bed && !withdrawn && (
                            <div className="text-[11px] brand-orange flex items-center gap-1 mt-0.5">
                              <BedDouble size={11} /> Dorm {e.dormitory_bed.room}/{e.dormitory_bed.bed} · {formatINR(e.dormitory_fee)}
                            </div>
                          )}
                          {withdrawn && (
                            <div className="text-[10px] font-mono uppercase tracking-widest text-red-400 mt-0.5">
                              Withdrawn {e.exit_date} · {e.withdrawal_reason}
                            </div>
                          )}
                        </div>
                        {!withdrawn ? (
                          <button onClick={() => setWithdrawEnrol(e)} data-testid={`withdraw-${e.id}`}
                            className="text-[10px] font-mono uppercase tracking-widest border border-red-500/40 text-red-400 px-2 py-1 hover:bg-red-500 hover:text-white shrink-0">
                            Withdraw
                          </button>
                        ) : (
                          <button onClick={() => reactivate(e.id)} data-testid={`reactivate-${e.id}`}
                            className="text-[10px] font-mono uppercase tracking-widest border border-green-500/40 text-green-400 px-2 py-1 hover:bg-green-500 hover:text-black shrink-0">
                            Reactivate
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                  {p.enrollments.length === 0 && <div className="text-white/40 text-sm">None</div>}
                </div>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-3">Recent Invoices</div>
                  {p.invoices.slice(0, 5).map((inv) => {
                    const paid = inv.payment_status === "paid" || inv.status === "paid";
                    const refunded = inv.status === "refunded" || (inv.refunded_amount && inv.refunded_amount >= (inv.amount_paid || inv.total)*0.99);
                    const refundable = inv.is_refundable === true && paid && !refunded;
                    return (
                      <div key={inv.id} className="text-sm mb-3 border-b border-white/6 pb-2 last:border-b-0" data-testid={`inv-${inv.id}`}>
                        <div className="flex justify-between items-center">
                          <span className="font-mono text-xs">
                            {inv.invoice_number}
                            {inv.is_refundable && <span className="ml-1 text-[9px] uppercase tracking-widest brand-orange">· refundable</span>}
                          </span>
                          <span className={paid ? (refunded ? "text-white/40 line-through" : "brand-blue") : "text-yellow-400"}>
                            {formatINR(inv.total)}
                          </span>
                        </div>
                        <div className="text-[10px] text-white/40 font-mono">{inv.description || inv.service_type}</div>
                        {!paid && (
                          <div className="flex gap-2 mt-1.5 flex-wrap">
                            <CollectPaymentButton invoiceId={inv.id} />
                            <CashCollectButton invoiceId={inv.id} amount={inv.balance_due ?? inv.total} onDone={load} />
                          </div>
                        )}
                        {refundable && (
                          <div className="flex gap-2 mt-1.5">
                            <RefundButton invoiceId={inv.id} amount={inv.amount_paid || inv.total} onDone={load} />
                          </div>
                        )}
                        {refunded && (
                          <div className="text-[10px] font-mono uppercase tracking-widest text-green-400 mt-1">
                            Refunded → {inv.refund_destination || "wallet"}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
        {parents.length === 0 && <div className="text-white/40 text-center py-12">No parents registered yet. Use &quot;Create parent&quot; to add one.</div>}
      </div>

      {enrollParent && (
        <EnrollModal
          parent={enrollParent}
          batches={batches}
          dormBeds={dormBeds}
          onClose={() => setEnrollParent(null)}
          onDone={() => { setEnrollParent(null); load(); }}
        />
      )}

      {showBulk && (
        <BulkImportModal
          onClose={() => setShowBulk(false)}
          onDone={() => load()}
        />
      )}

      {walletFor && (
        <WardWallet wardId={walletFor.id} wardName={walletFor.name} onClose={() => setWalletFor(null)} />
      )}

      {addWardParent && (
        <AddWardModal parent={addWardParent} onClose={() => setAddWardParent(null)}
          onDone={() => { setAddWardParent(null); load(); }} />
      )}

      {withdrawEnrol && (
        <WithdrawModal enrol={withdrawEnrol} onClose={() => setWithdrawEnrol(null)}
          onDone={() => { setWithdrawEnrol(null); load(); }} />
      )}

      {kycFor && (
        <KycVaultModal
          target={kycFor.target}
          id={kycFor.id}
          name={kycFor.name}
          onClose={() => { setKycFor(null); load(); }}
        />
      )}
    </div>
  );
}

// ------ Add Ward (admin-only) ------
function AddWardModal({ parent, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", age: "", sport: "cricket", dob: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/parent/wards", {
        parent_id: parent.id,
        name: form.name.trim(),
        age: Number(form.age),
        sport: form.sport,
        dob: form.dob || null,
        notes: form.notes || null,
      });
      toast.success(`Ward added under ${parent.name}. Now click Enrol to assign a batch.`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Add ward failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#131A2B] border border-white/12 my-8" onClick={(e) => e.stopPropagation()} data-testid="add-ward-modal">
        <div className="p-5 border-b border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Add Ward</div>
          <div className="font-display text-lg font-black uppercase mt-0.5">Under {parent.name}</div>
        </div>
        <div className="p-5 space-y-3">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ward name" data-testid="ward-name" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="number" min="3" max="30" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="Age" data-testid="ward-age" className="bg-black/40 border border-white/15 px-3 py-2.5 text-sm" />
            <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} data-testid="ward-sport" className="bg-black/40 border border-white/15 px-3 py-2.5 text-sm">
              <option value="cricket">Cricket</option>
              <option value="football">Football</option>
              <option value="badminton">Badminton</option>
              <option value="swimming">Swimming</option>
              <option value="tennis">Tennis</option>
              <option value="athletics">Athletics</option>
            </select>
          </div>
          <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm" placeholder="DOB" />
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          <div className="text-[11px] text-white/50 border-l-2 border-brand-blue pl-2">
            After adding the ward, use the <strong>Enrol</strong> button to assign a batch (life-academy / academy / hostel / any other).
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="ward-submit" className="flex-1 bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-50">
            {busy ? "Saving…" : "Add Ward"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ------ Withdraw enrolment ------
function WithdrawModal({ enrol, onClose, onDone }) {
  const [form, setForm] = useState({
    exit_date: new Date().toISOString().slice(0, 10),
    reason: "shifted", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Withdraw ${enrol.ward_name} from ${enrol.batch_name}? Historical records are kept; the bed (if any) is freed.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/enrollments/${enrol.id}/withdraw`, form);
      toast.success("Enrolment withdrawn");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Withdraw failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#131A2B] border border-red-500/40 my-8" onClick={(e) => e.stopPropagation()} data-testid="withdraw-modal">
        <div className="p-5 border-b border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">// Withdraw Enrolment</div>
          <div className="font-display text-lg font-black uppercase mt-0.5">{enrol.ward_name} · {enrol.batch_name}</div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Exit date</label>
            <input required type="date" value={form.exit_date} onChange={(e) => setForm({ ...form, exit_date: e.target.value })} data-testid="withdraw-date" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Reason</label>
            <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="withdraw-reason" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm">
              <option value="shifted">Shifted city / school</option>
              <option value="financial">Financial reasons</option>
              <option value="performance">Not a good fit</option>
              <option value="academic">Academic pressure</option>
              <option value="other">Other</option>
            </select>
          </div>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" data-testid="withdraw-notes"
            className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          <div className="text-[11px] text-white/50 border-l-2 border-red-500/60 pl-2">
            Historical enrolment + invoices remain. Dormitory bed (if any) will be freed. Ward and parent accounts stay intact.
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="withdraw-submit" className="flex-1 bg-red-500 text-white py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-red-600 disabled:opacity-50">
            {busy ? "Withdrawing…" : "Withdraw"}
          </button>
        </div>
      </form>
    </div>
  );
}
